// /var/www/oszczedzam/client/src/components/transactions/ImportTransactions.js
import React, { useState } from 'react';
import { 
  Box, Typography, Button, Paper, CircularProgress, 
  Alert, Stepper, Step, StepLabel, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, FormControl,
  InputLabel, Select, MenuItem, Checkbox, FormControlLabel,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Grid, Chip
} from '@mui/material';
import { CloudUpload as CloudUploadIcon, Warning as WarningIcon } from '@mui/icons-material';
import Papa from 'papaparse';
import { useTransactions } from '../../hooks/useTransactions';

// Kategorie
const predefinedCategories = [
  'Mieszkanie', 'Transport', 'Żywność', 'Rozrywka', 'Zdrowie', 
  'Odzież', 'Edukacja', 'Prezenty', 'Podróże', 'Dzieci', 'Sport', 
  'Zwierzęta', 'Elektronika', 'Rachunki', 'Subskrypcje', 'Inne',
  'Wynagrodzenie', 'Inwestycje', 'Premia', 'Sprzedaż'
];

const ImportTransactions = ({ onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [mappings, setMappings] = useState({
    date: '',
    title: '',
    amount: '',
    category: ''
  });
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [processedTransactions, setProcessedTransactions] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [importSummary, setImportSummary] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [existingTransactions, setExistingTransactions] = useState([]);
  
  const { addTransaction, transactions } = useTransactions();
  
  const steps = ['Wybierz plik', 'Mapuj kolumny', 'Podgląd i import'];

  // Funkcja sprawdzająca duplikaty
  const checkForDuplicates = (newTransactions, existingTrans = transactions) => {
    const duplicateList = [];
    
    newTransactions.forEach((newTrans, index) => {
      const potentialDuplicate = existingTrans.find(existing => {
        // Sprawdzenie na podstawie daty, tytułu i kwoty
        const sameDate = existing.date === newTrans.date;
        const sameTitle = existing.title && newTrans.title && 
                         existing.title.toLowerCase().trim() === newTrans.title.toLowerCase().trim();
        const sameAmount = Math.abs(existing.amount - newTrans.amount) < 0.01; // Tolerancja na różnice w zaokrągleniu
        
        return sameDate && sameTitle && sameAmount;
      });
      
      if (potentialDuplicate) {
        duplicateList.push({
          index,
          newTransaction: newTrans,
          existingTransaction: potentialDuplicate
        });
      }
    });
    
    return duplicateList;
  };
  
  // Obsługa wyboru pliku
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Sprawdź typ pliku
      if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
        setError('Proszę wybrać plik CSV.');
        return;
      }
      
      // Sprawdź rozmiar pliku (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('Plik jest zbyt duży. Maksymalny rozmiar to 10MB.');
        return;
      }
      
      setFile(selectedFile);
      setError('');
      setActiveStep(1);
    }
  };
  
  // Funkcja do automatycznego rozpoznawania kategorii na podstawie opisu transakcji
  const detectCategory = (title, amount) => {
    if (!title) return amount >= 0 ? 'Inne' : 'Inne';
    
    const titleLower = title.toLowerCase();
    
    // Definicja reguł dla kategorii
    const categoryRules = [
      { keywords: ['żabka', 'biedronka', 'lidl', 'auchan', 'tesco', 'carrefour', 'sklep', 'market', 'spożywczy', 'spozywczy', 'zakupy'], category: 'Żywność' },
      { keywords: ['restauracja', 'pizza', 'mcdonalds', 'kfc', 'burger', 'kebab', 'bar mleczny', 'sushi'], category: 'Rozrywka' },
      { keywords: ['apteka', 'lekarz', 'przychodnia', 'szpital', 'dentysta', 'leki'], category: 'Zdrowie' },
      { keywords: ['paliwo', 'bp', 'orlen', 'shell', 'lotos', 'stacja', 'parking', 'pkp', 'bilet', 'komunikacja', 'mpk', 'ztm'], category: 'Transport' },
      { keywords: ['czynsz', 'mieszkanie', 'woda', 'prąd', 'gaz', 'energia', 'internet', 'telewizja', 'telefon', 'opłata'], category: 'Mieszkanie' },
      { keywords: ['kino', 'teatr', 'koncert', 'festiwal', 'netflix', 'spotify', 'hbo', 'rozrywka'], category: 'Rozrywka' },
      { keywords: ['szkoła', 'kurs', 'nauka', 'książka', 'podręcznik', 'studia'], category: 'Edukacja' },
      { keywords: ['wynagrodzenie', 'wypłata', 'pensja', 'przelew wynagrodzenia', 'płaca', 'zlecenie'], category: 'Wynagrodzenie' },
      { keywords: ['premia', 'bonus', 'nagroda'], category: 'Premia' },
      { keywords: ['hotel', 'wakacje', 'wczasy', 'biuro podróży', 'lot', 'wyjazd', 'urlop'], category: 'Podróże' },
      { keywords: ['ubranie', 'odzież', 'buty', 'h&m', 'zara', 'house', 'reserved'], category: 'Odzież' },
      { keywords: ['prezent', 'urodziny', 'imieniny', 'święta'], category: 'Prezenty' },
      { keywords: ['dziecko', 'dzieci', 'żłobek', 'przedszkole', 'zabawki', 'pieluchy', 'szkoła'], category: 'Dzieci' },
      { keywords: ['sport', 'siłownia', 'fitness', 'rower', 'narty', 'basen'], category: 'Sport' },
      { keywords: ['pies', 'kot', 'weterynarz', 'zwierzę', 'karma'], category: 'Zwierzęta' },
      { keywords: ['telefon', 'laptop', 'komputer', 'tablet', 'elektronika', 'raty', 'agd', 'rtv'], category: 'Elektronika' },
      { keywords: ['prąd', 'gaz', 'woda', 'śmieci', 'czynsz', 'opłaty'], category: 'Rachunki' },
      { keywords: ['subskrypcja', 'netflix', 'spotify', 'youtube', 'microsoft', 'google', 'amazon'], category: 'Subskrypcje' },
    ];
    
    // Sprawdź czy opis pasuje do którejś z kategorii
    for (const rule of categoryRules) {
      if (rule.keywords.some(keyword => titleLower.includes(keyword))) {
        return rule.category;
      }
    }
    
    // Jeśli nie znaleziono pasującej kategorii, zwróć domyślną
    return 'Inne';
  };
  
  // Analiza pliku CSV
  const parseCSV = (callback) => {
    setLoading(true);
    setError('');
    
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const csvData = event.target.result;
        
        // Użyj Papaparse do parsowania pliku CSV z mBanku
        Papa.parse(csvData, {
          header: true,
          skipEmptyLines: true,
          delimiter: ";", // mBank używa średnika jako separatora
          encoding: "UTF-8",
          transformHeader: (header) => {
            // Usuń znaki # z nagłówków mBanku
            return header.replace(/^#/, '').trim();
          },
          complete: (results) => {
            if (results.data && results.data.length > 0) {
              console.log("Sparsowane dane:", results);
              setParsedData(results.data);
              
              // Automatyczne sugerowanie mapowań na podstawie nagłówków mBanku
              const headers = Object.keys(results.data[0]);
              console.log("Nagłówki:", headers);
              
              // Mapowanie typowych nagłówków z mBanku
              const suggestedMappings = {
                date: headers.find(h => /data|date/i.test(h)) || '',
                title: headers.find(h => /opis|description|operacji/i.test(h)) || '',
                amount: headers.find(h => /kwota|amount|value/i.test(h)) || '',
                category: '' // Nie mapujemy kategorii, będziemy używać automatycznego wykrywania
              };
              
              setMappings(suggestedMappings);
              setLoading(false);
              
              if (callback) callback();
            } else {
              setError('Nie znaleziono danych w pliku CSV.');
              setLoading(false);
            }
          },
          error: (error) => {
            console.error("Błąd parsowania:", error);
            setError(`Błąd analizy pliku: ${error.message}`);
            setLoading(false);
          }
        });
      } catch (error) {
        console.error("Wyjątek podczas parsowania:", error);
        setError(`Błąd odczytu pliku: ${error.message}`);
        setLoading(false);
      }
    };
    
    reader.onerror = () => {
      setError('Błąd odczytu pliku.');
      setLoading(false);
    };
    
    reader.readAsText(file, 'UTF-8');
  };
  
  // Przetwarzanie danych przed importem
  const processData = () => {
    setLoading(true);
    
    try {
      const processed = parsedData.map((row, index) => {
        // Pobierz wartości z odpowiednich kolumn
        const dateValue = row[mappings.date] || '';
        const titleValue = row[mappings.title] || '';
        const amountValue = row[mappings.amount] || '';
        
        // Przetwórz datę - mBank używa formatu DD.MM.YYYY
        let formattedDate = '';
        if (dateValue) {
          const dateParts = dateValue.split('.');
          if (dateParts.length === 3) {
            const day = dateParts[0].padStart(2, '0');
            const month = dateParts[1].padStart(2, '0');
            const year = dateParts[2];
            formattedDate = `${year}-${month}-${day}`; // Format YYYY-MM-DD
            
            // Sprawdź czy data nie jest z przyszłości
            const transactionDate = new Date(formattedDate);
            const today = new Date();
            today.setHours(23, 59, 59, 999); // Koniec dzisiaj
            
            if (transactionDate > today) {
              console.warn(`Transakcja z przyszłej daty: ${formattedDate}`);
              // Możemy zdecydować czy odrzucić lub zastąpić dzisiejszą datą
              // formattedDate = new Date().toISOString().split('T')[0];
            }
          } else {
            formattedDate = new Date().toISOString().split('T')[0]; // Domyślnie dzisiejsza data
          }
        } else {
          formattedDate = new Date().toISOString().split('T')[0];
        }
        
        // Przetwórz kwotę - mBank używa formatu "XXX,XX PLN"
        let formattedAmount = 0;
        if (amountValue) {
          // Usuń symbol waluty i spacje, zamień przecinki na kropki
          const cleanAmount = amountValue.toString().replace(/\s+PLN$|\s+/g, '').replace(',', '.');
          formattedAmount = parseFloat(cleanAmount);
          
          if (isNaN(formattedAmount)) {
            console.log("Nie udało się przetworzyć kwoty:", amountValue);
            formattedAmount = 0;
          }
        }
        
        // Skip transakcji z zerowymi kwotami
        if (formattedAmount === 0) {
          return null;
        }
        
        // Automatyczne wykrywanie kategorii
        const detectedCategory = detectCategory(titleValue, formattedAmount);
        
        // Użyj pola "Opis operacji" jako opisu transakcji
        const description = titleValue || '';
        
        // Przygotuj skrócony tytuł - pierwszą część opisu (do 30 znaków)
        let shortTitle = description;
        if (description.length > 30) {
          // Znajdź pierwszy sensowny separator lub przetnij na 30 znakach
          const separators = [' - ', '  ', ' / '];
          let cutIndex = 30;
          
          for (const separator of separators) {
            const separatorIndex = description.indexOf(separator);
            if (separatorIndex > 0 && separatorIndex < cutIndex) {
              cutIndex = separatorIndex;
            }
          }
          
          shortTitle = description.substring(0, cutIndex).trim();
          // Dodaj wielokropek, jeśli obcięto
          if (shortTitle.length < description.length) {
            shortTitle += '...';
          }
        }
        
        return {
          id: index,
          date: formattedDate,
          title: shortTitle || 'Transakcja importowana',
          description: description,
          amount: formattedAmount,
          category: detectedCategory,
          isRecurring: false,
          tags: [], // Można rozszerzyć o automatyczne tagowanie
          isSelected: true // Domyślnie wszystkie wiersze są zaznaczone
        };
      }).filter(transaction => transaction !== null); // Usuń null wartości
      
      console.log("Przetworzone dane:", processed.slice(0, 3));
      
      // Sprawdź duplikaty
      const duplicateList = checkForDuplicates(processed);
      setDuplicates(duplicateList);
      
      // Oznacz duplikaty
      const processedWithDuplicateFlag = processed.map((transaction, index) => {
        const isDuplicate = duplicateList.some(dup => dup.index === index);
        return {
          ...transaction,
          isDuplicate,
          isSelected: !isDuplicate // Domyślnie nie zaznaczaj duplikatów
        };
      });
      
      setProcessedTransactions(processedWithDuplicateFlag);
      setSelectedRows(processedWithDuplicateFlag
        .filter(row => row.isSelected)
        .map(row => row.id)
      );
      setLoading(false);
      setActiveStep(2);
    } catch (error) {
      console.error("Błąd przetwarzania:", error);
      setError(`Błąd przetwarzania danych: ${error.message}`);
      setLoading(false);
    }
  };
  
  // Import zaznaczonych transakcji
  const importTransactions = async () => {
    setLoading(true);
    setError('');
    
    try {
      const selectedTransactions = processedTransactions.filter(
        transaction => selectedRows.includes(transaction.id)
      );
      
      if (selectedTransactions.length === 0) {
        setError('Nie wybrano żadnych transakcji do importu.');
        setLoading(false);
        return;
      }
      
      let successful = 0;
      let failed = 0;
      let skipped = 0;
      
      // Dodaj transakcje do Firebase
      for (const transaction of selectedTransactions) {
        try {
          // Pomiń duplikaty jeśli użytkownik nie chce ich importować
          if (transaction.isDuplicate) {
            skipped++;
            continue;
          }
          
          const { id, isSelected, isDuplicate, ...transactionData } = transaction;
          await addTransaction(transactionData);
          successful++;
        } catch (error) {
          console.error('Błąd importu transakcji:', error);
          failed++;
        }
      }
      
      // Podsumowanie importu
      setImportSummary({
        total: selectedTransactions.length,
        successful,
        failed,
        skipped
      });
      
      setLoading(false);
      
      // Zamknij dialog po zakończeniu importu
      if (onImportComplete) {
        onImportComplete({
          total: selectedTransactions.length,
          successful,
          failed,
          skipped
        });
      }
    } catch (error) {
      setError(`Błąd importu: ${error.message}`);
      setLoading(false);
    }
  };
  
  // Obsługa zmiany kroku
  const handleNext = () => {
    if (activeStep === 0) {
      if (!file) {
        setError('Wybierz plik CSV do importu.');
        return;
      }
      parseCSV(() => setActiveStep(1));
    } else if (activeStep === 1) {
      if (!mappings.date || !mappings.title || !mappings.amount) {
        setError('Określ mapowanie dla wymaganych pól: Data, Tytuł i Kwota.');
        return;
      }
      processData();
    } else if (activeStep === 2) {
      setConfirmDialogOpen(true);
    }
  };
  
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };
  
  // Obsługa zaznaczania wierszy
  const handleRowSelect = (id) => {
    setSelectedRows(prev => {
      if (prev.includes(id)) {
        return prev.filter(rowId => rowId !== id);
      } else {
        return [...prev, id];
      }
    });
  };
  
  // Obsługa zaznaczania wszystkich wierszy
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      setSelectedRows(processedTransactions.map(row => row.id));
    }
    setSelectAll(!selectAll);
  };

  // Obsługa zaznaczania tylko nie-duplikatów
  const handleSelectNonDuplicates = () => {
    const nonDuplicateIds = processedTransactions
      .filter(row => !row.isDuplicate)
      .map(row => row.id);
    setSelectedRows(nonDuplicateIds);
  };
  
  // Formatuj kwotę
  const formatAmount = (amount) => {
    return amount.toLocaleString('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    });
  };
  
  // Renderuj zawartość odpowiedniego kroku
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="contained-button-file"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="contained-button-file">
              <Button 
                variant="contained" 
                component="span"
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 2 }}
              >
                Wybierz plik CSV
              </Button>
            </label>
            {file && (
              <Typography variant="body1" sx={{ mt: 2 }}>
                Wybrany plik: {file.name}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Obsługiwane są pliki CSV z mBanku. Upewnij się, że plik zawiera informacje o transakcjach.
              <br />
              Maksymalny rozmiar pliku: 10MB
            </Typography>
          </Box>
        );
      
      case 1:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="h6" gutterBottom>
              Mapowanie kolumn
            </Typography>
            <Typography variant="body2" paragraph>
              Dopasuj kolumny z pliku CSV do pól w aplikacji:
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* Mapowanie daty */}
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Data</InputLabel>
                  <Select
                    value={mappings.date}
                    onChange={(e) => setMappings({ ...mappings, date: e.target.value })}
                    label="Data"
                  >
                    <MenuItem value="">
                      <em>Wybierz kolumnę</em>
                    </MenuItem>
                    {parsedData.length > 0 && 
                      Object.keys(parsedData[0]).map((header) => (
                        <MenuItem key={header} value={header}>
                          {header}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Mapowanie tytułu */}
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Tytuł</InputLabel>
                  <Select
                    value={mappings.title}
                    onChange={(e) => setMappings({ ...mappings, title: e.target.value })}
                    label="Tytuł"
                  >
                    <MenuItem value="">
                      <em>Wybierz kolumnę</em>
                    </MenuItem>
                    {parsedData.length > 0 && 
                      Object.keys(parsedData[0]).map((header) => (
                        <MenuItem key={header} value={header}>
                          {header}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Mapowanie kwoty */}
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Kwota</InputLabel>
                  <Select
                    value={mappings.amount}
                    onChange={(e) => setMappings({ ...mappings, amount: e.target.value })}
                    label="Kwota"
                  >
                    <MenuItem value="">
                      <em>Wybierz kolumnę</em>
                    </MenuItem>
                    {parsedData.length > 0 && 
                      Object.keys(parsedData[0]).map((header) => (
                        <MenuItem key={header} value={header}>
                          {header}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Kategorie transakcji zostaną przypisane automatycznie na podstawie tytułów transakcji.
            </Alert>
            
            {/* Podgląd danych */}
            <Typography variant="h6" gutterBottom>
              Podgląd danych
            </Typography>
            
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {parsedData.length > 0 && 
                      Object.keys(parsedData[0]).map((header) => (
                        <TableCell key={header}>
                          {header}
                          {header === mappings.date && <Chip label="Data" size="small" color="primary" sx={{ ml: 1 }} />}
                          {header === mappings.title && <Chip label="Tytuł" size="small" color="primary" sx={{ ml: 1 }} />}
                          {header === mappings.amount && <Chip label="Kwota" size="small" color="primary" sx={{ ml: 1 }} />}
                        </TableCell>
                      ))
                    }
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedData.slice(0, 5).map((row, index) => (
                    <TableRow key={index}>
                      {Object.keys(row).map((key) => (
                        <TableCell key={key}>{row[key]}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Typography variant="caption" color="text.secondary">
              Wyświetlono 5 pierwszych wierszy z {parsedData.length} transakcji.
            </Typography>
          </Box>
        );
      
      case 2:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="h6" gutterBottom>
              Podgląd transakcji do importu
            </Typography>
            
            {/* Statystyki i ostrzeżenia */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">
                    {processedTransactions.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Wszystkie transakcje
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h6" color="success.main">
                    {processedTransactions.filter(t => !t.isDuplicate).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Nowe transakcje
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h6" color="warning.main">
                    {duplicates.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Potencjalne duplikaty
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h6" color="info.main">
                    {selectedRows.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Do importu
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Ostrzeżenia o duplikatach */}
            {duplicates.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
                Znaleziono {duplicates.length} potencjalnych duplikatów. Transakcje oznaczone jako duplikaty są domyślnie odznaczone.
                Sprawdź je dokładnie przed importem.
              </Alert>
            )}

            {/* Opcje zaznaczenia */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                }
                label="Zaznacz/odznacz wszystkie"
              />
              {duplicates.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectNonDuplicates}
                >
                  Zaznacz tylko nowe
                </Button>
              )}
            </Box>
            
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell>Tytuł</TableCell>
                    <TableCell>Kategoria</TableCell>
                    <TableCell align="right">Kwota</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processedTransactions.map((transaction) => (
                    <TableRow 
                      key={transaction.id}
                      sx={{ 
                        backgroundColor: transaction.isDuplicate ? 'warning.light' : 'inherit',
                        opacity: transaction.isDuplicate ? 0.7 : 1
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.includes(transaction.id)}
                          onChange={() => handleRowSelect(transaction.id)}
                        />
                      </TableCell>
                      <TableCell>
                        {transaction.isDuplicate ? (
                          <Chip 
                            label="Duplikat?" 
                            size="small" 
                            color="warning"
                            icon={<WarningIcon />}
                          />
                        ) : (
                          <Chip 
                            label="Nowa" 
                            size="small" 
                            color="success"
                          />
                        )}
                      </TableCell>
                      <TableCell>{transaction.date}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {transaction.title}
                          </Typography>
                          {transaction.description && transaction.description !== transaction.title && (
                            <Typography variant="caption" color="text.secondary">
                              {transaction.description.length > 50 
                                ? `${transaction.description.substring(0, 50)}...` 
                                : transaction.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={transaction.category} size="small" />
                      </TableCell>
                      <TableCell 
                        align="right"
                        sx={{ 
                          color: transaction.amount >= 0 ? 'success.main' : 'error.main',
                          fontWeight: 'medium'
                        }}
                      >
                        {formatAmount(transaction.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Typography variant="body2" color="text.secondary">
              Zaznaczono {selectedRows.length} z {processedTransactions.length} transakcji.
            </Typography>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              Wszystkie transakcje będą importowane z automatycznie wykrytymi kategoriami. Możesz edytować kategorie po imporcie.
              {duplicates.length > 0 && (
                <> Transakcje oznaczone jako duplikaty nie będą importowane, chyba że je zaznaczysz.</>
              )}
            </Alert>
          </Box>
        );
      
      default:
        return null;
    }
  };
  
  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Import transakcji z mBanku
      </Typography>
      
      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      {/* Komunikat o błędzie */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Komunikat o udanym imporcie */}
      {importSummary && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Pomyślnie zaimportowano {importSummary.successful} z {importSummary.total} transakcji.
          {importSummary.failed > 0 && ` ${importSummary.failed} transakcji nie udało się zaimportować.`}
          {importSummary.skipped > 0 && ` Pominięto ${importSummary.skipped} duplikatów.`}
        </Alert>
      )}
      
      {/* Zawartość kroku */}
      <Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>
              {activeStep === 1 ? 'Analizowanie pliku...' : 
               activeStep === 2 ? 'Przetwarzanie danych...' : 
               'Importowanie transakcji...'}
            </Typography>
          </Box>
        ) : (
          renderStepContent()
        )}
      </Box>
      
      {/* Przyciski nawigacji */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        {activeStep > 0 && (
          <Button onClick={handleBack} sx={{ mr: 1 }} disabled={loading}>
            Wstecz
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={loading}
        >
          {activeStep === steps.length - 1 ? 'Importuj' : 'Dalej'}
        </Button>
      </Box>
      
      {/* Dialog potwierdzenia importu */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Potwierdź import</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Czy na pewno chcesz zaimportować {selectedRows.length} transakcji do aplikacji?
          </DialogContentText>
          
          {/* Szczegóły importu */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>Szczegóły importu:</Typography>
            <Typography variant="body2" color="text.secondary">
              • Nowe transakcje: {processedTransactions.filter(t => !t.isDuplicate && selectedRows.includes(t.id)).length}
            </Typography>
            {duplicates.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                • Duplikaty do importu: {processedTransactions.filter(t => t.isDuplicate && selectedRows.includes(t.id)).length}
              </Typography>
            )}
          </Box>
          
          {duplicates.length > 0 && processedTransactions.filter(t => t.isDuplicate && selectedRows.includes(t.id)).length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Uwaga: Wybrano do importu transakcje oznaczone jako duplikaty. 
              Może to spowodować wystąpienie powtarzających się transakcji w aplikacji.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Anuluj</Button>
          <Button onClick={() => {
            setConfirmDialogOpen(false);
            importTransactions();
          }} variant="contained" autoFocus>
            Importuj
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ImportTransactions;